"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RefreshCw, WifiOff, X } from "lucide-react";

import { trackEvent } from "@/lib/analytics";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type PwaPlatform =
  | "ios"
  | "android"
  | "macos"
  | "windows"
  | "linux"
  | "other";

type InstallResult = "accepted" | "dismissed" | "manual" | "installed";

interface PwaContextValue {
  platform: PwaPlatform;
  isOnline: boolean;
  isStandalone: boolean;
  canPromptInstall: boolean;
  updateAvailable: boolean;
  install: () => Promise<InstallResult>;
  applyUpdate: () => void;
  dismissUpdate: () => void;
  setAppBadge: (count?: number) => Promise<void>;
}

const PwaContext = createContext<PwaContextValue | null>(null);
const UPDATE_DISMISSAL_KEY = "fexr:pwa-dismissed-update";
const UPDATE_SNOOZE_KEY = "fexr:pwa-update-snoozed-at";
// Every deploy ships a new worker version, so a dismissal snoozes all
// update prompts for a day rather than just the version that was dismissed.
const UPDATE_SNOOZE_MS = 24 * 60 * 60 * 1000;

function hashText(source: string): string {
  let hash = 2166136261;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

async function getWorkerVersion(worker: ServiceWorker): Promise<string> {
  try {
    const version = await new Promise<string>((resolve, reject) => {
      const channel = new MessageChannel();
      const timeout = window.setTimeout(
        () => reject(new Error("Service worker version timed out")),
        1500,
      );
      channel.port1.onmessage = (event) => {
        window.clearTimeout(timeout);
        const nextVersion = event.data?.version;
        if (typeof nextVersion === "string" && nextVersion) {
          resolve(nextVersion);
        } else {
          reject(new Error("Service worker version is unavailable"));
        }
      };
      worker.postMessage({ type: "PWA_GET_VERSION" }, [channel.port2]);
    });
    return `manifest:${version}`;
  } catch {
    try {
      const response = await fetch(worker.scriptURL, { cache: "no-store" });
      if (response.ok) return `script:${hashText(await response.text())}`;
    } catch {}
    // Legacy workers cannot report a version. Limit their prompt to once per
    // day instead of showing it again on every page load.
    return `legacy:${Math.floor(Date.now() / 86_400_000)}`;
  }
}

function detectPlatform(): PwaPlatform {
  if (typeof navigator === "undefined") return "other";

  const ua = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() || "";
  const isTouchMac = platform === "macintel" && navigator.maxTouchPoints > 1;

  if (/iphone|ipad|ipod/.test(ua) || isTouchMac) return "ios";
  if (/android/.test(ua)) return "android";
  if (/win/.test(platform) || /windows/.test(ua)) return "windows";
  if (/mac/.test(platform) || /macintosh/.test(ua)) return "macos";
  if (/linux/.test(platform) || /linux/.test(ua)) return "linux";
  return "other";
}

function detectStandalone() {
  if (typeof window === "undefined") return false;
  const iosStandalone = Boolean(
    (window.navigator as Navigator & { standalone?: boolean }).standalone,
  );
  return (
    iosStandalone ||
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.matchMedia("(display-mode: window-controls-overlay)").matches
  );
}

const capture = trackEvent;

// Error text never goes into analytics: a short code instead.
function reasonCode(error: unknown): string {
  const name = error instanceof Error ? error.name : "";
  if (name === "NotAllowedError") return "not_allowed";
  if (name === "AbortError") return "aborted";
  if (name === "InvalidStateError") return "invalid_state";
  if (name === "SecurityError") return "security";
  if (name === "NetworkError" || name === "TypeError") return "network";
  return "other";
}

export function PwaProvider({ children }: { children: React.ReactNode }) {
  const [platform, setPlatform] = useState<PwaPlatform>("other");
  const [isOnline, setIsOnline] = useState(true);
  const [isStandalone, setIsStandalone] = useState(false);
  const [installPrompt, setInstallPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(
    null,
  );
  const [waitingWorkerVersion, setWaitingWorkerVersion] = useState<
    string | null
  >(null);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const activatingUpdate = useRef(false);

  useEffect(() => {
    setPlatform(detectPlatform());
    setIsOnline(navigator.onLine);
    setIsStandalone(detectStandalone());

    const displayMode = window.matchMedia("(display-mode: standalone)");
    const onDisplayModeChange = () => setIsStandalone(detectStandalone());
    const onOnline = () => {
      setIsOnline(true);
      capture("pwa.connection_restored");
    };
    const onOffline = () => {
      setIsOnline(false);
      capture("pwa.offline");
    };
    const onBeforeInstall = (rawEvent: Event) => {
      const event = rawEvent as BeforeInstallPromptEvent;
      event.preventDefault();
      setInstallPrompt(event);
      capture("pwa.install_available", { platform: detectPlatform() });
    };
    const onInstalled = () => {
      setInstallPrompt(null);
      setIsStandalone(true);
      capture("pwa.installed", { platform: detectPlatform() });
    };

    displayMode.addEventListener?.("change", onDisplayModeChange);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    if (detectStandalone()) {
      capture("pwa.standalone_launch", { platform: detectPlatform() });
    }

    return () => {
      displayMode.removeEventListener?.("change", onDisplayModeChange);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (
      process.env.NODE_ENV !== "production" ||
      !("serviceWorker" in navigator)
    ) {
      return;
    }

    let disposed = false;
    let registration: ServiceWorkerRegistration | undefined;
    let observedWaitingWorker: ServiceWorker | null = null;

    const announceWaitingWorker = async (worker: ServiceWorker) => {
      if (observedWaitingWorker === worker) return;
      observedWaitingWorker = worker;
      const version = await getWorkerVersion(worker);
      if (disposed) return;
      let dismissed = false;
      try {
        const snoozedAt = Number(localStorage.getItem(UPDATE_SNOOZE_KEY));
        dismissed =
          localStorage.getItem(UPDATE_DISMISSAL_KEY) === version ||
          (snoozedAt > 0 && Date.now() - snoozedAt < UPDATE_SNOOZE_MS);
      } catch {}
      setWaitingWorker(worker);
      setWaitingWorkerVersion(version);
      setUpdateDismissed(dismissed);
      if (!dismissed) capture("pwa.update_available", { version });
    };

    const inspectRegistration = (next: ServiceWorkerRegistration) => {
      registration = next;
      if (next.waiting && navigator.serviceWorker.controller) {
        void announceWaitingWorker(next.waiting);
      }

      next.addEventListener("updatefound", () => {
        const installing = next.installing;
        installing?.addEventListener("statechange", () => {
          if (
            installing.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            void announceWaitingWorker(next.waiting || installing);
          }
        });
      });
    };

    navigator.serviceWorker
      .register("/sw.js", { scope: "/", updateViaCache: "none" })
      .then((next) => {
        if (disposed) return;
        inspectRegistration(next);
        capture("pwa.worker_registered");
      })
      .catch((error) => {
        capture("pwa.worker_registration_failed", {
          reason: reasonCode(error),
        });
      });

    const checkForUpdate = () => {
      if (document.visibilityState === "visible") {
        registration?.update().catch(() => {});
      }
    };
    const onControllerChange = () => {
      if (!activatingUpdate.current) return;
      activatingUpdate.current = false;
      window.location.reload();
    };

    document.addEventListener("visibilitychange", checkForUpdate);
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      onControllerChange,
    );

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", checkForUpdate);
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        onControllerChange,
      );
    };
  }, []);

  const install = useCallback(async (): Promise<InstallResult> => {
    if (detectStandalone()) return "installed";
    if (!installPrompt) {
      capture("pwa.manual_install_help_opened", { platform });
      return "manual";
    }

    capture("pwa.install_prompted", { platform });
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    setInstallPrompt(null);
    capture("pwa.install_prompt_result", {
      platform,
      outcome: choice.outcome,
    });
    return choice.outcome;
  }, [installPrompt, platform]);

  const applyUpdate = useCallback(() => {
    if (!waitingWorker) return;
    activatingUpdate.current = true;
    capture("pwa.update_accepted");
    waitingWorker.postMessage({ type: "SKIP_WAITING" });
  }, [waitingWorker]);

  const dismissUpdate = useCallback(() => {
    if (waitingWorkerVersion) {
      try {
        localStorage.setItem(UPDATE_DISMISSAL_KEY, waitingWorkerVersion);
      } catch {}
    }
    try {
      localStorage.setItem(UPDATE_SNOOZE_KEY, String(Date.now()));
    } catch {}
    setUpdateDismissed(true);
  }, [waitingWorkerVersion]);

  const setAppBadge = useCallback(async (count?: number) => {
    const badgeNavigator = navigator as Navigator & {
      setAppBadge?: (value?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };

    if (typeof count === "number" && count > 0) {
      await badgeNavigator.setAppBadge?.(count);
    } else {
      await badgeNavigator.clearAppBadge?.();
    }
  }, []);

  const value = useMemo<PwaContextValue>(
    () => ({
      platform,
      isOnline,
      isStandalone,
      canPromptInstall: Boolean(installPrompt),
      updateAvailable: Boolean(waitingWorker) && !updateDismissed,
      install,
      applyUpdate,
      dismissUpdate,
      setAppBadge,
    }),
    [
      applyUpdate,
      dismissUpdate,
      install,
      installPrompt,
      isOnline,
      isStandalone,
      platform,
      setAppBadge,
      updateDismissed,
      waitingWorker,
    ],
  );

  return (
    <PwaContext.Provider value={value}>
      {children}

      {!isOnline && (
        <div className="pwa-safe-top fixed inset-x-0 top-0 z-[100] flex items-center justify-center gap-2 bg-warning px-4 py-2 text-center text-xs font-semibold text-foreground sm:text-sm">
          <WifiOff aria-hidden="true" className="h-4 w-4" />
          Offline — live data and financial actions are unavailable.
        </div>
      )}

      {Boolean(waitingWorker) && !updateDismissed && (
        <aside
          aria-live="polite"
          className="pwa-safe-bottom fixed bottom-4 left-1/2 z-[100] flex h-9 max-w-[calc(100%-2rem)] -translate-x-1/2 items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] pl-3 pr-1 text-xs text-foreground shadow-[0_8px_32px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-2xl backdrop-saturate-150"
        >
          <RefreshCw
            aria-hidden="true"
            className="h-3.5 w-3.5 shrink-0 text-primary"
          />
          <span className="truncate">Update ready</span>
          <button
            className="h-7 shrink-0 rounded-full bg-primary/90 px-3 text-xs font-medium text-primary-foreground transition-colors duration-150 hover:bg-primary"
            onClick={applyUpdate}
            type="button"
          >
            Reload
          </button>
          <button
            aria-label="Dismiss update"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors duration-150 hover:bg-white/10 hover:text-foreground"
            onClick={dismissUpdate}
            type="button"
          >
            <X aria-hidden="true" className="h-3.5 w-3.5" />
          </button>
        </aside>
      )}
    </PwaContext.Provider>
  );
}

export function usePwa() {
  const context = useContext(PwaContext);
  if (!context) throw new Error("usePwa must be used inside PwaProvider");
  return context;
}
